import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Gift, 
  Plus, 
  Edit, 
  Trash2, 
  TrendingUp,
  Save,
  X,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CouponManager = () => {
  // Helper to get auth token (supports both user and admin)
  const getAuthToken = () => localStorage.getItem('token') || localStorage.getItem('admin_token');
  
  const [hasAccess, setHasAccess] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [viewingAnalytics, setViewingAnalytics] = useState(null);

  const [formData, setFormData] = useState({
    code: '',
    description: '',
    partnerBusinessName: '',
    partnerBusinessContact: '',
    expiryDate: '',
    monthlyLimit: 50,
    assignedBusinesses: [],
    termsAndConditions: '',
    active: true
  });

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const token = getAuthToken();
      await axios.get(`${API}/review-manager/access-check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasAccess(true);
      loadCoupons();
      loadBusinesses();
    } catch (error) {
      setHasAccess(false);
      setLoading(false);
    }
  };

  const loadCoupons = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API}/review-manager/coupons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoupons(response.data.coupons);
    } catch (error) {
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const loadBusinesses = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API}/review-manager/businesses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinesses(response.data.businesses);
    } catch (error) {
      console.error('Failed to load businesses');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.code || !formData.partnerBusinessName || !formData.expiryDate) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const token = getAuthToken();

      if (editingCoupon) {
        await axios.put(
          `${API}/review-manager/coupons/${editingCoupon.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Coupon updated successfully!');
      } else {
        await axios.post(
          `${API}/review-manager/coupons`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Coupon created successfully!');
      }

      setShowForm(false);
      setEditingCoupon(null);
      resetForm();
      loadCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save coupon');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      description: '',
      partnerBusinessName: '',
      partnerBusinessContact: '',
      expiryDate: '',
      monthlyLimit: 50,
      assignedBusinesses: [],
      termsAndConditions: '',
      active: true
    });
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      description: coupon.description,
      partnerBusinessName: coupon.partnerBusinessName,
      partnerBusinessContact: coupon.partnerBusinessContact || '',
      expiryDate: coupon.expiryDate,
      monthlyLimit: coupon.monthlyLimit,
      assignedBusinesses: coupon.assignedBusinesses || [],
      termsAndConditions: coupon.termsAndConditions || '',
      active: coupon.active !== false
    });
    setShowForm(true);
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;

    try {
      const token = getAuthToken();
      await axios.delete(`${API}/review-manager/coupons/${couponId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Coupon deleted');
      loadCoupons();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const toggleBusinessAssignment = (businessId) => {
    const current = formData.assignedBusinesses;
    if (current.includes(businessId)) {
      setFormData({
        ...formData,
        assignedBusinesses: current.filter(id => id !== businessId)
      });
    } else {
      setFormData({
        ...formData,
        assignedBusinesses: [...current, businessId]
      });
    }
  };

  const viewAnalytics = async (couponId) => {
    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${API}/review-manager/coupons/${couponId}/analytics`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setViewingAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    }
  };

  if (hasAccess === null || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (hasAccess === false) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
              <p className="text-gray-600 mb-4">
                This Coupon Manager is not available for your account.
              </p>
              <p className="text-sm text-gray-500">
                Please make sure you're logged in with potraitchocolate@gmail.com
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <Gift className="w-8 h-8 text-purple-600" />
                Coupon Manager
              </h1>
              <p className="text-gray-600 mt-2">
                Create and manage coupons with monthly limits and business assignments
              </p>
            </div>
            <Button
              onClick={() => {
                resetForm();
                setEditingCoupon(null);
                setShowForm(true);
              }}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Coupon
            </Button>
          </div>
        </div>

        {/* Form */}
        {showForm && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>
                {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Coupon Code *</label>
                    <Input
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="SAVE20"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Partner Business Name *</label>
                    <Input
                      value={formData.partnerBusinessName}
                      onChange={(e) => setFormData({ ...formData, partnerBusinessName: e.target.value })}
                      placeholder="Coffee Shop Name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Partner Contact</label>
                    <Input
                      value={formData.partnerBusinessContact}
                      onChange={(e) => setFormData({ ...formData, partnerBusinessContact: e.target.value })}
                      placeholder="Phone or email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Monthly Limit *</label>
                    <Input
                      type="number"
                      value={formData.monthlyLimit}
                      onChange={(e) => setFormData({ ...formData, monthlyLimit: parseInt(e.target.value) })}
                      min="1"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Max coupons to give per month</p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2">Description *</label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="20% off any purchase"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Expiry Date *</label>
                    <Input
                      value={formData.expiryDate}
                      onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                      placeholder="December 31, 2024"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2">Terms & Conditions</label>
                    <textarea
                      value={formData.termsAndConditions}
                      onChange={(e) => setFormData({ ...formData, termsAndConditions: e.target.value })}
                      className="w-full p-2 border rounded-lg"
                      rows="2"
                      placeholder="One per customer. Cannot be combined with other offers."
                    />
                  </div>
                </div>

                {/* Assign to Businesses */}
                <div>
                  <label className="block text-sm font-medium mb-3">
                    Assign to Businesses ({formData.assignedBusinesses.length} selected)
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto border rounded-lg p-3">
                    {businesses.map((business) => (
                      <label
                        key={business.id}
                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.assignedBusinesses.includes(business.id)}
                          onChange={() => toggleBusinessAssignment(business.id)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{business.businessName}</span>
                      </label>
                    ))}
                    {businesses.length === 0 && (
                      <p className="text-sm text-gray-500 col-span-full p-4">
                        No businesses yet. Create businesses first to assign coupons.
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label htmlFor="active" className="text-sm font-medium">Active</label>
                </div>

                <div className="flex gap-3">
                  <Button type="submit" className="flex-1">
                    <Save className="w-4 h-4 mr-2" />
                    {editingCoupon ? 'Update Coupon' : 'Create Coupon'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowForm(false);
                      setEditingCoupon(null);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Coupons List */}
        {!showForm && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coupons.map((coupon) => {
              const isExpiringSoon = new Date(coupon.expiryDate) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
              
              return (
                <Card key={coupon.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span className="font-mono text-lg">{coupon.code}</span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        coupon.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {coupon.active ? 'Active' : 'Inactive'}
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{coupon.description}</p>
                      <p className="text-sm text-gray-600 mt-1">Partner: {coupon.partnerBusinessName}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-blue-50 p-2 rounded">
                        <p className="font-semibold">{coupon.usedThisMonth || 0}/{coupon.monthlyLimit}</p>
                        <p className="text-gray-600">This Month</p>
                      </div>
                      <div className="bg-purple-50 p-2 rounded">
                        <p className="font-semibold">{coupon.totalUsed || 0}</p>
                        <p className="text-gray-600">Total Used</p>
                      </div>
                    </div>

                    {isExpiringSoon && (
                      <div className="flex items-center gap-1 text-xs text-orange-600 bg-orange-50 p-2 rounded">
                        <AlertCircle className="w-3 h-3" />
                        <span>Expires: {coupon.expiryDate}</span>
                      </div>
                    )}

                    <div className="text-xs text-gray-600">
                      <p>Assigned to {coupon.assignedBusinesses?.length || 0} businesses</p>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => viewAnalytics(coupon.id)}
                        className="flex-1"
                      >
                        <TrendingUp className="w-3 h-3 mr-1" />
                        Stats
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(coupon)}
                        className="flex-1"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(coupon.id)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {coupons.length === 0 && (
              <div className="col-span-full text-center py-12">
                <Gift className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">No coupons created yet</p>
                <Button onClick={() => setShowForm(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Coupon
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Analytics Modal */}
        {viewingAnalytics && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <Card className="max-w-2xl w-full">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Analytics: {viewingAnalytics.couponCode}</span>
                  <Button variant="ghost" size="sm" onClick={() => setViewingAnalytics(null)}>
                    <X className="w-4 h-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold">{viewingAnalytics.totalRedemptions}</p>
                    <p className="text-sm text-gray-600">Total</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold">{viewingAnalytics.thisMonthRedemptions}</p>
                    <p className="text-sm text-gray-600">This Month</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold">{viewingAnalytics.remainingThisMonth}</p>
                    <p className="text-sm text-gray-600">Remaining</p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <p className="text-2xl font-bold">{viewingAnalytics.assignedToBusinesses}</p>
                    <p className="text-sm text-gray-600">Businesses</p>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="font-semibold mb-2">Monthly Limit Status</p>
                  <div className="relative w-full bg-gray-200 rounded-full h-4">
                    <div
                      className={`h-4 rounded-full ${
                        viewingAnalytics.thisMonthRedemptions >= viewingAnalytics.monthlyLimit
                          ? 'bg-red-500'
                          : viewingAnalytics.thisMonthRedemptions > viewingAnalytics.monthlyLimit * 0.8
                          ? 'bg-yellow-500'
                          : 'bg-green-500'
                      }`}
                      style={{
                        width: `${Math.min(100, (viewingAnalytics.thisMonthRedemptions / viewingAnalytics.monthlyLimit) * 100)}%`
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    {viewingAnalytics.thisMonthRedemptions} of {viewingAnalytics.monthlyLimit} used
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default CouponManager;
